// Menu Component - Multiple Layout Support
// Layouts: classic, sidebar, scroll, bistro, compact
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ItemModal } from './ItemModal';

// Parse modifiers from item
function getItemModifiers(item) {
  if (!item.itemOptions_json) return [];
  try {
    const options = typeof item.itemOptions_json === 'string' 
      ? JSON.parse(item.itemOptions_json) 
      : item.itemOptions_json;
    return options || [];
  } catch {
    return [];
  }
}

// Whether a category is enabled for display. menuCat in the online getMenu
// response currently carries no visibility field, so categories default to
// visible. If the getMenu SELECT later adds `active` or `status` to menuCat,
// this honors it automatically (matching how items use `active`).
function isCategoryEnabled(cat) {
  if (cat == null) return false;
  if (cat.active != null) return cat.active === 1 || cat.active === '1' || cat.active === true;
  if (cat.status != null) return cat.status === 1 || cat.status === '1' || cat.status === true;
  return true;
}

// ---------------------------------------------------------------------------
// Sales-channel gating
//
// This is the ONLINE ordering web app, so it represents the "Online" channel
// (id 3 in the getMenu `sources` list). A category's `salesChannels_json`
// controls which channels it appears on. Canonical shape mirrors the item-level
// orderSources_json:
//   [{ name, price, types: ['POS','Online','Delivery','Kiosk'] }]
// but the parser is intentionally tolerant of simpler forms too.
// ---------------------------------------------------------------------------
// The channel token this app filters on (case-insensitive). A category/item is
// shown only when its salesChannels_json / orderSources_json includes it.
// Your data uses these type values: POS, Kiosk, Delivery, 3PD, 3PO, Catering.
//   Beer/Wine = [POS, Kiosk]                               -> in-store only, hide online
//   Food      = [POS, Kiosk, Delivery, 3PD, 3PO, Catering] -> show online
// So the online-ordering channel is whichever token Food has and Beer/Wine
// lacks. >>> CONFIRM THIS VALUE: set it to the exact token for this site. <<<
const THIS_SALES_CHANNEL    = 'delivery'; // e.g. 'delivery' | '3po' | '3pd' | 'catering'
const THIS_SALES_CHANNEL_ID = 3;          // legacy numeric fallback (unused by current data)

// Flatten salesChannels_json (any reasonable shape) into a Set of lowercased
// channel tokens. Returns null when nothing is configured, which callers treat
// as "no restriction -> visible".
function parseSalesChannelTokens(raw) {
  if (raw == null || raw === '') return null;

  let val = raw;
  if (typeof val === 'string') {
    const s = val.trim();
    try {
      val = JSON.parse(s);
    } catch {
      // Plain CSV string like "POS,Online"
      val = s.split(',').map(t => t.trim()).filter(Boolean);
    }
  }

  const tokens = new Set();
  const addToken = (t) => {
    if (t == null) return;
    tokens.add(String(t).trim().toLowerCase());
  };

  const ingest = (entry) => {
    if (entry == null) return;
    if (typeof entry === 'string' || typeof entry === 'number') {
      addToken(entry);
    } else if (Array.isArray(entry)) {
      entry.forEach(ingest);
    } else if (typeof entry === 'object') {
      // Canonical: { name, price, types: [...] }. Also handle { channels: [...] }.
      if (Array.isArray(entry.types))    entry.types.forEach(addToken);
      if (Array.isArray(entry.channels)) entry.channels.forEach(addToken);
      // Category salesChannels_json uses the singular shape { type: 'POS' }.
      if (entry.type != null) addToken(entry.type);
      // A bare { name: 'Online' } channel entry counts too. Guarded so it does
      // NOT fire for item orderSources_json entries, whose `name` is the ITEM
      // name (not a channel) and which always carry `types`/`type`.
      if (entry.name != null && entry.type == null &&
          !Array.isArray(entry.types) && !Array.isArray(entry.channels)) {
        addToken(entry.name);
      }
    }
  };

  ingest(val);
  // No tokens found (including [] / {} / whitespace) -> null, which every gate
  // treats as "unconfigured -> show".
  return tokens.size ? tokens : null;
}

// True if the category should appear on THIS app's sales channel.
// No salesChannels_json configured -> visible (don't hide anything by default).
function isCategoryOnSalesChannel(cat) {
  const tokens = parseSalesChannelTokens(cat && cat.salesChannels_json);
  if (!tokens) return true; // unconfigured (null/''/[]) = available everywhere
  return tokens.has(THIS_SALES_CHANNEL) || tokens.has(String(THIS_SALES_CHANNEL_ID));
}

// True if the item should appear on THIS app's sales channel, per its
// orderSources_json ([{ name, price, types:[...], visibleTo }]).
// null / '' / [] / no channels listed -> visible (don't hide by default).
function isItemOnSalesChannel(item) {
  const tokens = parseSalesChannelTokens(item && item.orderSources_json);
  if (!tokens) return true; // unconfigured (null/''/[]) = available everywhere
  return tokens.has(THIS_SALES_CHANNEL) || tokens.has(String(THIS_SALES_CHANNEL_ID));
}

// ---------------------------------------------------------------------------
// Item day/time availability
//
// Rule: an item's availableDays_json time windows gate visibility ONLY when the
// item is NOT pinned to a specific source — i.e. its orderSources_json is
// unspecified (null/empty) OR its source entry covers "all"/this channel. If an
// item explicitly targets sources, we let that source gating decide and do not
// also apply the day/time window (the source config is the authority then).
//
// availableDays_json shape (same as the venue-level one App.jsx parses):
//   [{ days: ['Mon','Tue',...], timeFrom: 'HH:MM', timeTo: 'HH:MM' }, ...]
// null / empty / unparseable -> always available (no restriction).
// ---------------------------------------------------------------------------
const DOW_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Does this item's source config leave day/time as the deciding factor?
// True when orderSources_json is null/empty, or an entry's types include
// 'all' or this channel ('online'). When sources explicitly list channels that
// do NOT include this one, the item shouldn't be here at all — but that's the
// job of the source/sales-channel gate, not this function; we return true so we
// don't double-filter, and let source gating handle exclusion.
function sourceLeavesTimingToItem(item) {
  const raw = item && item.orderSources_json;
  if (raw == null || raw === '') return true; // unspecified -> use item day/time
  let val = raw;
  if (typeof val === 'string') {
    const s = val.trim();
    if (!s) return true;
    try { val = JSON.parse(s); } catch { return true; }
  }
  if (Array.isArray(val) && val.length === 0) return true; // [] -> unspecified
  const tokens = parseSalesChannelTokens(val);
  if (!tokens) return true;
  // 'all' or this channel present -> day/time window applies.
  return tokens.has('all') || tokens.has(THIS_SALES_CHANNEL) || tokens.has(String(THIS_SALES_CHANNEL_ID));
}

function parseAvailableWindows(raw) {
  if (raw == null || raw === '') return null;
  let val = raw;
  if (typeof val === 'string') {
    const s = val.trim();
    if (!s) return null;
    try { val = JSON.parse(s); } catch { return null; }
  }
  if (!Array.isArray(val)) val = [val];
  return val.length ? val : null;
}

// Resolve "now" into the VENUE's local day-of-week + minutes-of-day, so item
// windows are evaluated in the venue's timezone, not the customer's browser.
// `tz` is an IANA name (e.g. 'America/Los_Angeles') from getOnlineData's
// venue.timezone. Falls back to local time if tz is missing/invalid.
function venueNowParts(now, tz) {
  const d = now || new Date();
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(d);
      let wd = null, hh = null, mm = null;
      for (const p of parts) {
        if (p.type === 'weekday') wd = p.value;            // 'Mon','Tue',...
        else if (p.type === 'hour') hh = parseInt(p.value, 10);
        else if (p.type === 'minute') mm = parseInt(p.value, 10);
      }
      // Intl can emit '24' for midnight under hour12:false in some engines.
      if (hh === 24) hh = 0;
      if (wd && Number.isFinite(hh) && Number.isFinite(mm)) {
        return { today: wd, cur: hh * 100 + mm };
      }
    } catch (e) { /* bad tz -> fall through to browser-local */ }
  }
  return { today: DOW_ABBR[d.getDay()], cur: d.getHours() * 100 + d.getMinutes() };
}

// Is `item` available at time `now` (a Date), evaluated in venue timezone `tz`?
// Applies only when the item's source config leaves timing to the item
// (see sourceLeavesTimingToItem).
function isItemAvailableNow(item, now, tz) {
  const windows = parseAvailableWindows(item && item.availableDays_json);
  if (!windows) return true;                 // no windows configured -> always
  if (!sourceLeavesTimingToItem(item)) return true; // source-pinned -> not our call

  const { today, cur } = venueNowParts(now, tz);

  for (const w of windows) {
    if (!w) continue;
    const days = Array.isArray(w.days) ? w.days : null;
    // If days listed and today isn't one, this window doesn't apply.
    if (days && days.length && !days.includes(today)) continue;

    const from = w.timeFrom, to = w.timeTo;
    // No times on the window = available all day on the listed day(s).
    if (!from || !to) return true;
    const [fh, fm] = String(from).split(':').map(Number);
    const [th, tm] = String(to).split(':').map(Number);
    if ([fh, fm, th, tm].some(n => Number.isNaN(n))) return true; // bad data -> don't hide
    const openT = fh * 100 + fm;
    const closeT = th * 100 + tm;
    if (openT <= closeT) {
      if (cur >= openT && cur <= closeT) return true;
    } else {
      // Overnight window (e.g. 22:00 -> 02:00)
      if (cur >= openT || cur <= closeT) return true;
    }
  }
  return false; // had windows, none matched now -> hidden
}

export function Menu({ 
  categories, 
  items, 
  menuGroups, 
  selectedCategory, 
  onCategorySelect, 
  onAddToCart,
  settings,
  timezone, // IANA venue timezone (e.g. 'America/Los_Angeles') for item day/time windows
  layout = 'classic' // classic, sidebar, scroll, bistro, compact
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const scrollContainerRef = useRef(null);
  const categoryRefs = useRef({});
  const [activeScrollCategory, setActiveScrollCategory] = useState(null);

  // Ticking "now" so item day/time availability re-evaluates as windows open
  // and close, without requiring a page reload. Updates every 60s.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNowTick(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  // Category ids allowed on this sales channel (enabled AND on-channel). An item
  // whose CATEGORY is hidden must not appear anywhere — including search results.
  // We also track known category ids so an item pointing at a category that
  // isn't in the list at all (orphan/data mismatch) is NOT hidden on this basis.
  const { channelAllowedCategoryIds, knownCategoryIds } = useMemo(() => {
    const allowed = new Set();
    const known = new Set();
    (categories || []).forEach(cat => {
      known.add(cat.menucat);
      if (isCategoryEnabled(cat) && isCategoryOnSalesChannel(cat)) allowed.add(cat.menucat);
    });
    return { channelAllowedCategoryIds: allowed, knownCategoryIds: known };
  }, [categories]);

  const itemCategoryVisible = useCallback((item) => {
    const cid = item && item.menucatid;
    if (!knownCategoryIds.has(cid)) return true;        // orphan -> don't hide here
    return channelAllowedCategoryIds.has(cid);          // known -> must be allowed
  }, [channelAllowedCategoryIds, knownCategoryIds]);

  // Filter items by category and search
  const filteredItems = useMemo(() => {
    let filtered = items.filter(item =>
      (item.active === 1 || item.active === '1') &&
      isItemOnSalesChannel(item) &&
      itemCategoryVisible(item) &&
      isItemAvailableNow(item, nowTick, timezone)
    );
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.item_name.toLowerCase().includes(query) ||
        (item.description && item.description.toLowerCase().includes(query))
      );
    } else if (selectedCategory && layout !== 'scroll' && layout !== 'bistro') {
      filtered = filtered.filter(item => item.menucatid === selectedCategory);
    }
    
    return filtered.sort((a, b) => (a.sortorder || 0) - (b.sortorder || 0));
  }, [items, selectedCategory, searchQuery, layout, nowTick, timezone, itemCategoryVisible]);

  // Group items by category for scroll/bistro layouts
  const itemsByCategory = useMemo(() => {
    if (layout !== 'scroll' && layout !== 'bistro') return {};
    
    const grouped = {};
    const activeItems = items.filter(item =>
      (item.active === 1 || item.active === '1') &&
      isItemOnSalesChannel(item) &&
      itemCategoryVisible(item) &&
      isItemAvailableNow(item, nowTick, timezone)
    );
    
    categories.forEach(cat => {
      const catItems = activeItems
        .filter(item => item.menucatid === cat.menucat)
        .sort((a, b) => (a.sortorder || 0) - (b.sortorder || 0));
      if (catItems.length > 0) {
        grouped[cat.menucat] = catItems;
      }
    });
    
    return grouped;
  }, [items, categories, layout, nowTick, timezone, itemCategoryVisible]);

  // Set of category ids that have at least one active AND currently-available
  // item (layout-independent). A category whose items are all outside their
  // day/time window right now will drop out of the nav too.
  const categoriesWithItems = useMemo(() => {
    const set = new Set();
    items.forEach(item => {
      if ((item.active === 1 || item.active === '1') &&
          isItemOnSalesChannel(item) &&
          itemCategoryVisible(item) &&
          isItemAvailableNow(item, nowTick, timezone)) {
        set.add(item.menucatid);
      }
    });
    return set;
  }, [items, nowTick, timezone, itemCategoryVisible]);

  // Categories actually shown anywhere (nav + content): enabled, on this sales
  // channel, AND non-empty. A category that is disabled, excluded from the
  // online channel, or has only inactive items won't render.
  const visibleCategories = useMemo(() => {
    return categories.filter(cat =>
      isCategoryEnabled(cat) &&
      isCategoryOnSalesChannel(cat) &&
      categoriesWithItems.has(cat.menucat)
    );
  }, [categories, categoriesWithItems]);

  // If the selected category became hidden (e.g. its items were turned off),
  // move selection to the first visible one so the menu isn't stuck on an empty
  // view. Only relevant to layouts that filter by selectedCategory.
  useEffect(() => {
    if (layout === 'scroll' || layout === 'bistro') return;
    if (!visibleCategories.length) return;
    const stillVisible = visibleCategories.some(c => c.menucat === selectedCategory);
    if (!stillVisible) {
      onCategorySelect(visibleCategories[0].menucat);
    }
  }, [visibleCategories, selectedCategory, layout, onCategorySelect]);

  // Set initial active category for scroll layout
  useEffect(() => {
    if (layout === 'scroll' && !activeScrollCategory && categories.length > 0) {
      const firstCatWithItems = visibleCategories[0];
      if (firstCatWithItems) {
        setActiveScrollCategory(firstCatWithItems.menucat);
      }
    }
  }, [layout, visibleCategories, activeScrollCategory]);

  // Handle scroll spy for scroll layout
  useEffect(() => {
    if (layout !== 'scroll') return;
    
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      
      const scrollTop = container.scrollTop;
      let currentCategory = null;
      
      Object.keys(categoryRefs.current).forEach(catId => {
        const el = categoryRefs.current[catId];
        if (el && el.offsetTop <= scrollTop + 120) {
          currentCategory = catId;
        }
      });
      
      if (currentCategory && currentCategory !== activeScrollCategory) {
        setActiveScrollCategory(currentCategory);
      }
    };
    
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [layout, activeScrollCategory]);

  // Scroll to category
  const scrollToCategory = useCallback((catId) => {
    const el = categoryRefs.current[catId];
    if (el && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: el.offsetTop - 60,
        behavior: 'smooth'
      });
    }
    setActiveScrollCategory(catId);
  }, []);

  const handleItemClick = (item) => {
    const itemMods = getItemModifiers(item);
    const hasOptions = itemMods.length > 0 || item.mod_prompt;
    const hasReferences = itemMods.some(m => m.type === 'reference');
    
    if (hasOptions || hasReferences) {
      setSelectedItem({ ...item });
      setItemModalOpen(true);
    } else {
      const price = parseFloat(String(item.item_price).replace(/[$,]/g, '')) || 0;
      onAddToCart({
        id: Date.now() + Math.random(),
        itemid: item.itemid,
        quantity: 1,
        name: item.item_name,
        price: price,
        lineTotal: price,
        modifiers: [],
        modifierTotal: 0,
        specialInstructions: '',
        taxrate: parseFloat(item.taxrate) || 0.0825,
        _editMeta: { kind: 'simple', item, qty: 1 }
      });
    }
  };

  const handleAddFromModal = (cartItem) => {
    onAddToCart(cartItem);
    setItemModalOpen(false);
    setSelectedItem(null);
  };

  // Render search bar
  const renderSearch = () => (
    <div className="menu__search">
      <div className="menu__search-wrapper">
        <svg className="menu__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          type="text"
          className="menu__search-input"
          placeholder="Search menu..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="menu__search-clear" onClick={() => setSearchQuery('')}>×</button>
        )}
      </div>
    </div>
  );

  // ==========================================================================
  // CLASSIC LAYOUT - Top horizontal category tabs
  // ==========================================================================
  if (layout === 'classic') {
    return (
      <div className="menu menu--classic">
        {renderSearch()}
        
        {!searchQuery && (
          <div className="menu__categories">
            <div className="menu__categories-scroll">
              {visibleCategories.map((cat) => (
                <button
                  key={cat.menucat}
                  className={`menu__category ${selectedCategory === cat.menucat ? 'menu__category--active' : ''}`}
                  onClick={() => onCategorySelect(cat.menucat)}
                >
                  {cat.menu_name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="menu__items">
          {filteredItems.length === 0 ? (
            <div className="menu__empty"><p>No items found</p></div>
          ) : (
            <div className="menu__grid">
              {filteredItems.map((item) => (
                <MenuItemCard key={item.itemid} item={item} onClick={() => handleItemClick(item)} />
              ))}
            </div>
          )}
        </div>

        {selectedItem && (
          <ItemModal
            open={itemModalOpen}
            item={selectedItem}
            modGroups={menuGroups}
            onClose={() => { setItemModalOpen(false); setSelectedItem(null); }}
            onAdd={handleAddFromModal}
          />
        )}
      </div>
    );
  }

  // ==========================================================================
  // SIDEBAR LAYOUT - Categories on left sidebar
  // ==========================================================================
  if (layout === 'sidebar') {
    return (
      <div className="menu menu--sidebar">
        <div className="menu__sidebar">
          <div className="menu__sidebar-header">
            <h3>Menu</h3>
          </div>
          <nav className="menu__sidebar-nav">
            {visibleCategories.map((cat) => (
              <button
                key={cat.menucat}
                className={`menu__sidebar-item ${selectedCategory === cat.menucat ? 'menu__sidebar-item--active' : ''}`}
                onClick={() => onCategorySelect(cat.menucat)}
              >
                {cat.menu_name}
              </button>
            ))}
          </nav>
        </div>
        
        <div className="menu__main">
          {renderSearch()}
          
          <div className="menu__category-header">
            <h2>{categories.find(c => c.menucat === selectedCategory)?.menu_name || 'All Items'}</h2>
          </div>
          
          <div className="menu__items">
            {filteredItems.length === 0 ? (
              <div className="menu__empty"><p>No items found</p></div>
            ) : (
              <div className="menu__grid menu__grid--sidebar">
                {filteredItems.map((item) => (
                  <MenuItemCard key={item.itemid} item={item} onClick={() => handleItemClick(item)} />
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedItem && (
          <ItemModal
            open={itemModalOpen}
            item={selectedItem}
            modGroups={menuGroups}
            onClose={() => { setItemModalOpen(false); setSelectedItem(null); }}
            onAdd={handleAddFromModal}
          />
        )}
      </div>
    );
  }

  // ==========================================================================
  // SCROLL LAYOUT - Full menu, categories as anchors, scroll spy
  // ==========================================================================
  if (layout === 'scroll') {
    return (
      <div className="menu menu--scroll">
        {/* Sticky category nav */}
        <div className="menu__scroll-nav">
          <div className="menu__scroll-nav-inner">
            {visibleCategories.map((cat) => (
              <button
                key={cat.menucat}
                className={`menu__scroll-nav-item ${activeScrollCategory === cat.menucat ? 'menu__scroll-nav-item--active' : ''}`}
                onClick={() => scrollToCategory(cat.menucat)}
              >
                {cat.menu_name}
              </button>
            ))}
          </div>
        </div>

        {renderSearch()}
        
        <div className="menu__scroll-content" ref={scrollContainerRef}>
          {searchQuery ? (
            <div className="menu__items">
              {filteredItems.length === 0 ? (
                <div className="menu__empty"><p>No items found</p></div>
              ) : (
                <div className="menu__list">
                  {filteredItems.map((item) => (
                    <MenuItemRow key={item.itemid} item={item} onClick={() => handleItemClick(item)} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            visibleCategories.map((cat) => (
              <div 
                key={cat.menucat} 
                className="menu__section"
                ref={el => categoryRefs.current[cat.menucat] = el}
              >
                <h2 className="menu__section-title">{cat.menu_name}</h2>
                {cat.description && <p className="menu__section-desc">{cat.description}</p>}
                <div className="menu__list">
                  {itemsByCategory[cat.menucat]?.map((item) => (
                    <MenuItemRow key={item.itemid} item={item} onClick={() => handleItemClick(item)} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {selectedItem && (
          <ItemModal
            open={itemModalOpen}
            item={selectedItem}
            modGroups={menuGroups}
            onClose={() => { setItemModalOpen(false); setSelectedItem(null); }}
            onAdd={handleAddFromModal}
          />
        )}
      </div>
    );
  }

  // ==========================================================================
  // BISTRO LAYOUT - Elegant, full menu with large images and descriptions
  // ==========================================================================
  if (layout === 'bistro') {
    return (
      <div className="menu menu--bistro">
        {renderSearch()}
        
        <div className="menu__bistro-content">
          {searchQuery ? (
            <div className="menu__items">
              {filteredItems.length === 0 ? (
                <div className="menu__empty"><p>No items found</p></div>
              ) : (
                <div className="menu__bistro-grid">
                  {filteredItems.map((item) => (
                    <MenuItemBistro key={item.itemid} item={item} onClick={() => handleItemClick(item)} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            visibleCategories.map((cat) => (
              <div key={cat.menucat} className="menu__bistro-section">
                <div className="menu__bistro-section-header">
                  <h2>{cat.menu_name}</h2>
                  {cat.description && <p>{cat.description}</p>}
                </div>
                <div className="menu__bistro-grid">
                  {itemsByCategory[cat.menucat]?.map((item) => (
                    <MenuItemBistro key={item.itemid} item={item} onClick={() => handleItemClick(item)} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {selectedItem && (
          <ItemModal
            open={itemModalOpen}
            item={selectedItem}
            modGroups={menuGroups}
            onClose={() => { setItemModalOpen(false); setSelectedItem(null); }}
            onAdd={handleAddFromModal}
          />
        )}
      </div>
    );
  }

  // ==========================================================================
  // COMPACT LAYOUT - Dense list, minimal images
  // ==========================================================================
  if (layout === 'compact') {
    return (
      <div className="menu menu--compact">
        <div className="menu__compact-sidebar">
          {visibleCategories.map((cat) => (
            <button
              key={cat.menucat}
              className={`menu__compact-cat ${selectedCategory === cat.menucat ? 'menu__compact-cat--active' : ''}`}
              onClick={() => onCategorySelect(cat.menucat)}
            >
              {cat.menu_name}
            </button>
          ))}
        </div>
        
        <div className="menu__compact-main">
          {renderSearch()}
          
          <div className="menu__compact-items">
            {filteredItems.length === 0 ? (
              <div className="menu__empty"><p>No items found</p></div>
            ) : (
              <div className="menu__compact-list">
                {filteredItems.map((item) => (
                  <MenuItemCompact key={item.itemid} item={item} onClick={() => handleItemClick(item)} />
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedItem && (
          <ItemModal
            open={itemModalOpen}
            item={selectedItem}
            modGroups={menuGroups}
            onClose={() => { setItemModalOpen(false); setSelectedItem(null); }}
            onAdd={handleAddFromModal}
          />
        )}
      </div>
    );
  }

  return null;
}

// ==========================================================================
// MENU ITEM COMPONENTS FOR DIFFERENT LAYOUTS
// ==========================================================================

// Description for the item LIST: prefer a SHORT description; when there isn't
// one, fall back to the long description, which the card truncates with an
// ellipsis (clamp: true). The item modal shows the full long description.
// NOTE: confirm the short-description field name — candidates below.
function listDescription(item) {
  if (!item) return null;
  const short = (item.short_description || item.shortDescription
    || item.item_short_description || item.short_desc || item.descriptionShort || '').trim();
  if (short) return { text: short, clamp: false };
  const long = (item.description || item.item_desc || '').trim();
  if (long) return { text: long, clamp: true };
  return null;
}

// Two-line ellipsis clamp for the long-description fallback in list cards.
const LIST_DESC_CLAMP = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
};

// Card style (for classic and sidebar)
function MenuItemCard({ item, onClick }) {
  const price = parseFloat(item.item_price) || 0;
  const hasImage = item.image_url && item.image_url.trim();
  
  return (
    <button 
      className={`menu-item menu-item--card ${hasImage ? 'menu-item--has-image' : ''}`}
      onClick={onClick}
    >
      {hasImage && (
        <div className="menu-item__image">
          <img src={item.image_url} alt={item.item_name} loading="lazy" />
        </div>
      )}
      <div className="menu-item__content">
        <h3 className="menu-item__name">{item.item_name}</h3>
        {(() => { const _d = listDescription(item); return _d
          ? <p className="menu-item__desc" style={_d.clamp ? LIST_DESC_CLAMP : undefined}>{_d.text}</p>
          : null; })()}
        <div className="menu-item__footer">
          {price > 0 && <span className="menu-item__price">${price.toFixed(2)}</span>}
        </div>
      </div>
      <div className="menu-item__add">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      </div>
    </button>
  );
}

// Row style (for scroll layout)
function MenuItemRow({ item, onClick }) {
  const price = parseFloat(item.item_price) || 0;
  const hasImage = item.image_url && item.image_url.trim();
  
  return (
    <button className="menu-item menu-item--row" onClick={onClick}>
      <div className="menu-item__row-content">
        <h3 className="menu-item__name">{item.item_name}</h3>
        {(() => { const _d = listDescription(item); return _d
          ? <p className="menu-item__desc" style={_d.clamp ? LIST_DESC_CLAMP : undefined}>{_d.text}</p>
          : null; })()}
        {price > 0 && <span className="menu-item__price">${price.toFixed(2)}</span>}
      </div>
      {hasImage && (
        <div className="menu-item__row-image">
          <img src={item.image_url} alt={item.item_name} loading="lazy" />
        </div>
      )}
      <div className="menu-item__row-add">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      </div>
    </button>
  );
}

// Bistro style (elegant, large)
function MenuItemBistro({ item, onClick }) {
  const price = parseFloat(item.item_price) || 0;
  const hasImage = item.image_url && item.image_url.trim();
  
  return (
    <button className="menu-item menu-item--bistro" onClick={onClick}>
      {hasImage && (
        <div className="menu-item__bistro-image">
          <img src={item.image_url} alt={item.item_name} loading="lazy" />
        </div>
      )}
      <div className="menu-item__bistro-content">
        <div className="menu-item__bistro-header">
          <h3 className="menu-item__name">{item.item_name}</h3>
          {price > 0 && <span className="menu-item__price">${price.toFixed(2)}</span>}
        </div>
        {(() => { const _d = listDescription(item); return _d
          ? <p className="menu-item__desc" style={_d.clamp ? LIST_DESC_CLAMP : undefined}>{_d.text}</p>
          : null; })()}
      </div>
    </button>
  );
}

// Compact style (minimal)
function MenuItemCompact({ item, onClick }) {
  const price = parseFloat(item.item_price) || 0;
  
  return (
    <button className="menu-item menu-item--compact" onClick={onClick}>
      <span className="menu-item__name">{item.item_name}</span>
      {price > 0 && <span className="menu-item__dots"></span>}
      {price > 0 && <span className="menu-item__price">${price.toFixed(2)}</span>}
      <span className="menu-item__compact-add">+</span>
    </button>
  );
}

export default Menu;